import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
} from '@mui/material';
import {
  PersonAdd,
  Edit,
  CheckCircle,
  TrendingUp,
  Phone,
  Email,
  Assignment,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';

export interface ActivityItem {
  id: string;
  type: 'lead_created' | 'lead_updated' | 'status_changed' | 'lead_won' | 'lead_contacted' | 'note_added';
  title: string;
  description: string;
  user: string;
  timestamp: string;
  metadata?: {
    leadName?: string;
    oldStatus?: string;
    newStatus?: string;
    value?: number;
  };
}

interface RecentActivityProps {
  activities: ActivityItem[];
  maxItems?: number;
}

export const RecentActivity: React.FC<RecentActivityProps> = ({ activities, maxItems = 10 }) => {
  const getActivityIcon = (type: ActivityItem['type']) => {
    const iconProps = { sx: { fontSize: 20 } };
    switch (type) {
      case 'lead_created':
        return <PersonAdd {...iconProps} />;
      case 'lead_updated':
        return <Edit {...iconProps} />;
      case 'status_changed':
        return <TrendingUp {...iconProps} />;
      case 'lead_won':
        return <CheckCircle {...iconProps} />;
      case 'lead_contacted':
        return <Phone {...iconProps} />;
      case 'note_added':
        return <Assignment {...iconProps} />;
      default:
        return <Email {...iconProps} />;
    }
  };

  const getActivityColor = (type: ActivityItem['type']) => {
    switch (type) {
      case 'lead_created':
        return '#3b82f6';
      case 'lead_updated':
        return '#8b5cf6';
      case 'status_changed':
        return '#f59e0b';
      case 'lead_won':
        return '#22c55e';
      case 'lead_contacted':
        return '#06b6d4';
      case 'note_added':
        return '#64748b';
      default:
        return '#94a3b8';
    }
  };

  const displayActivities = activities.slice(0, maxItems);

  if (displayActivities.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No recent activity yet
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Start creating and managing leads to see activity here
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: '100%', p: 0 }}>
      {displayActivities.map((activity, index) => {
        const color = getActivityColor(activity.type);
        const timeAgo = formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true });

        return (
          <ListItem
            key={activity.id}
            sx={{
              px: 0,
              py: 2,
              borderBottom: index < displayActivities.length - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ListItemAvatar>
              <Avatar
                sx={{
                  backgroundColor: `${color}20`,
                  color: color,
                  width: 44,
                  height: 44,
                }}
              >
                {getActivityIcon(activity.type)}
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {activity.title}
                  </Typography>
                  {activity.metadata?.value && (
                    <Chip
                      label={`$${activity.metadata.value.toLocaleString()}`}
                      size="small"
                      sx={{
                        backgroundColor: `${color}20`,
                        color: color,
                        fontWeight: 700,
                        height: 24,
                      }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    {activity.description}
                  </Typography>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      by {activity.user}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {timeAgo}
                    </Typography>
                    {activity.metadata?.oldStatus && activity.metadata?.newStatus && (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          •
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={activity.metadata.oldStatus}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption">→</Typography>
                          <Chip
                            label={activity.metadata.newStatus}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: `${color}20`,
                              color: color,
                            }}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              }
            />
          </ListItem>
        );
      })}
    </List>
  );
};